import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Firestore,
  connectFirestoreEmulator,
  getFirestore,
} from 'firebase/firestore';
import { environment } from '../../../environments/environment';

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let emulatorConnected = false;

/**
 * Initialize Firebase and Firestore with emulator support
 * This ensures emulator connection happens before any Firestore operations
 */
export function initializeFirestore(): Firestore {
  console.log('[FirestoreInit] 🚀 initializeFirestore() called');

  if (db) {
    console.log('[FirestoreInit] ℹ️ Using existing Firestore instance');
    return db; // Return existing instance
  }

  console.log('[FirestoreInit] 🔄 Creating new Firestore instance...');

  // Initialize Firebase app
  if (!app) {
    app = initializeApp(environment.firebase);
    console.log(
      '[FirestoreInit] 🔥 Firebase app initialized with projectId:',
      environment.firebase.projectId
    );
  }

  // Initialize Firestore
  db = getFirestore(app);

  // Connect to emulator ONLY in local web dev (localhost) and NOT in native apps.
  // Capacitor native hosts the webview at https://localhost, so we must avoid the emulator there.
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '');

  const isNativeCapacitor =
    typeof window !== 'undefined' &&
    !!(window as any).Capacitor?.isNativePlatform?.() &&
    (window as any).Capacitor.isNativePlatform();

  if (
    !environment.production &&
    !emulatorConnected &&
    isLocalhost &&
    !isNativeCapacitor
  ) {
    try {
      // Only connect to emulator when running on localhost
      console.log('[FirestoreInit] 🔄 Connecting to Firestore emulator...');
      console.log('[FirestoreInit] 📍 Emulator: 127.0.0.1:8080');
      console.log('[FirestoreInit] 📊 Environment:', {
        production: environment.production,
        hostname:
          typeof window !== 'undefined' ? window.location.hostname : 'SSR',
        isLocalhost,
      });

      // Connect to emulator - this MUST be called before any operations
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      emulatorConnected = true;
      console.log(
        '[FirestoreInit] ✅ Connected to Firestore emulator at 127.0.0.1:8080'
      );
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      // Check if already connected (this is OK)
      if (
        errorMsg.includes('already been called') ||
        errorMsg.includes('already initialized') ||
        errorMsg.includes('has already been called')
      ) {
        emulatorConnected = true;
        console.log('[FirestoreInit] ℹ️ Emulator already connected (OK)');
      } else {
        // Log the error but don't fail - let Firestore try to connect
        console.error(
          '[FirestoreInit] ❌ Emulator connection error:',
          errorMsg
        );
        console.error('[FirestoreInit] Full error:', error);
        console.log('[FirestoreInit] ⚠️ Will try production Firestore');
      }
    }
  } else if (!environment.production && !isLocalhost) {
    console.log(
      '[FirestoreInit] ℹ️ Dev deployment - using dev Firestore project (not localhost)'
    );
    console.log(
      '[FirestoreInit] 📍 Hostname:',
      typeof window !== 'undefined' ? window.location.hostname : 'SSR'
    );
    console.log(
      '[FirestoreInit] 📍 Project ID:',
      environment.firebase.projectId
    );
  } else if (environment.production) {
    console.log(
      '[FirestoreInit] ℹ️ Production mode - using production Firestore'
    );
    console.log(
      '[FirestoreInit] 📍 Project ID:',
      environment.firebase.projectId
    );
  }

  // Log Firestore instance info for debugging
  console.log('[FirestoreInit] 📊 Firestore instance initialized:', {
    projectId: environment.firebase.projectId,
    connectedToEmulator: emulatorConnected,
    production: environment.production,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    isLocalhost: typeof window !== 'undefined' ? isLocalhost : false,
  });

  return db;
}
